/**
 * AuthHomeCTA — 로그인 사용자 홈 화면 하단 CTA 바
 *
 * 챗봇이 처음 열렸을 때(messages.length ≤ 1) 입력창 위에 노출되어
 * 두 가지 항상 가능한 액션을 제공한다.
 *
 *   1) 현재 화면에 대한 도움말 보기 — 호스트 페이지가 init 응답에 채운
 *      `context.help_url`을 새 탭으로 연다. URL이 없으면 비활성화.
 *   2) 불편/오류/건의사항 신고하기 — in-chat 피드백 폼 dispatch.
 *
 * 비로그인용 HomeCTA와 시각적 일관성을 위해 같은 칩 스타일을 사용한다.
 */

import { cn } from '@/lib/utils';

interface AuthHomeCTAProps {
  /**
   * 호스트 페이지가 init 응답에 주입한 현재 페이지 도움말 URL. 없으면 1번 버튼 비활성.
   *
   * exactOptionalPropertyTypes 환경에서 호출부가 `string | undefined`를 그대로 넘길 수 있도록
   * undefined를 명시적으로 허용한다.
   */
  helpUrl?: string | undefined;
  onShowHelp: () => void;
  onShowFeedback: () => void;
}

export function AuthHomeCTA({
  helpUrl,
  onShowHelp,
  onShowFeedback,
}: AuthHomeCTAProps) {
  const helpEnabled = Boolean(helpUrl && helpUrl.trim().length > 0);

  return (
    <div
      className={cn(
        'ml-flex ml-flex-col ml-gap-2',
        'ml-px-3.5 ml-py-3 ml-flex-shrink-0',
        'ml-bg-surface ml-border-t ml-border-line-soft'
      )}
    >
      <div
        className={cn(
          'ml-text-cb-xs ml-font-semibold ml-text-mute',
          'ml-tracking-wider ml-uppercase'
        )}
      >
        도움이 필요하세요?
      </div>
      <div className="ml-flex ml-flex-wrap ml-gap-1.5">
        <ChipButton
          label="현재 화면 도움말 보기"
          disabled={!helpEnabled}
          disabledHint="현재 페이지에 등록된 도움말이 없습니다"
          onClick={onShowHelp}
        />
        <ChipButton label="불편/오류/건의사항 신고하기" onClick={onShowFeedback} />
      </div>
    </div>
  );
}

interface ChipButtonProps {
  label: string;
  disabled?: boolean;
  disabledHint?: string;
  onClick: () => void;
}

function ChipButton({
  label,
  disabled = false,
  disabledHint,
  onClick,
}: ChipButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledHint : undefined}
      className={cn(
        'ml-px-3.5 ml-py-2',
        'ml-rounded-cb-md ml-border',
        'ml-text-cb-base ml-font-medium ml-whitespace-nowrap',
        'ml-shadow-sm ml-transition-all ml-duration-150',
        disabled
          ? 'ml-bg-surface ml-border-line ml-text-mute ml-cursor-not-allowed ml-opacity-60'
          : cn(
              'ml-bg-white ml-border-line ml-text-ink-secondary ml-cursor-pointer',
              'hover:ml-border-brand-600 hover:ml-bg-brand-50 hover:ml-text-brand-600',
              'active:ml-translate-y-px active:ml-shadow-none'
            )
      )}
    >
      {label}
    </button>
  );
}
